"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import React, { useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { toast } from "sonner"
import { match } from "ts-pattern"
import { signIn } from "next-auth/react"
import { apiService } from "@/lib/api"
import { Routes } from "@/lib/routes"
import { AccountSignupData } from "@/types/auth"
import { SignupFormInputs, signupFormSchema } from "@/utils/helpers/validators/auth/SignupFormSchema"
import { SocialLoginSection } from "../../SocialButtons"
import { PasswordInput } from "@/components/unified/PasswordInput"

const FormInput = ({
   label,
   name,
   type = "text",
   placeholder,
   autoFocus,
}: {
   label: string
   name: keyof SignupFormInputs
   type?: string
   placeholder?: string
   autoFocus?: boolean
}) => {
   const {
      register,
      formState: { errors },
   } = useFormContext<SignupFormInputs>()

   return (
      <div className="flex w-full flex-col gap-1.5">
         <label htmlFor={name} className="text-custom-sm-bold  text-gray-700">
            {label}
         </label>
         <input
            {...register(name)}
            id={name}
            type={type}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
         />
         {errors[name] && <span className="text-xs text-red-500">{errors[name]?.message}</span>}
      </div>
   )
}

const SignupForm = ({ autofocus }: { autofocus?: boolean }) => {
   const router = useRouter()
   const pathname = usePathname()
   const searchParams = useSearchParams()
   const user_group = searchParams.get("user_group")

   const methods = useForm<SignupFormInputs>({
      mode: "onSubmit",
      resolver: zodResolver(signupFormSchema),
      defaultValues: {
         email: "",
         firstName: "",
         lastName: "",
         password: "",
         company: "",
         phone: "",
      },
   })

   const [isLoading, setIsLoading] = useState(false)

   async function signupAndLogin(data: AccountSignupData) {
      return apiService.post(Routes.api.unifiedAccountSignupApi.getPath(), data)
   }

   async function onSubmit(form_data: SignupFormInputs) {
      if (isLoading) return
      setIsLoading(true)

      try {
         const user_groupVal = match(user_group)
            .with("job-seeker", () => "JobSeeker")
            .with("employer", () => "Employer")
            .otherwise(() => "Employer")

         const payload: AccountSignupData = {
            email: form_data.email.trim().toLowerCase(),
            firstName: form_data.firstName.trim(),
            lastName: form_data.lastName.trim(),
            company: form_data.company?.trim(),
            phone: form_data.phone?.trim(),
            password: form_data.password,
            user_group_id: user_groupVal,
         }

         const res = await signupAndLogin(payload)

         if (res.user) {
            await signIn("credentials", {
               redirect: false,
               email: payload.email,
               password: payload.password,
            })
         }

         toast.success("Successfully registered")
         let destination = pathname?.includes("unified") ? "/" : pathname || "/"
         router.push(destination)
         router.refresh()
      } catch (error: any) {
         const errorMessage = error?.message || ""

         if (errorMessage === "Email already exists") {
            toast.error("Already signed up. Please try to log in.")
         } else {
            toast.error(errorMessage || "Signup failed")
         }
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <div className="w-full max-w-md ">
         <h1 className="mb-3 text-4xl font-bold text-[#333333] md:mb-0 md:text-custom-4xl-bold">Create Your account</h1>
         <p className="leading-0 mb-[32px] text-custom-sm-normal text-[#333333]">
            Join American Restaurant Association and grow your business.
         </p>

         <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
               <FormInput label="Email" name="email" placeholder="email@example.com" autoFocus={autofocus} />

               <div className="grid grid-cols-2 gap-4 ">
                  <FormInput label="First Name" name="firstName" placeholder="First Name" />
                  <FormInput label="Last Name" name="lastName" placeholder="Last Name" />
               </div>

               <FormInput label="Company" name="company" placeholder="Restaurant Name" />
               <FormInput label="Phone Number" name="phone" placeholder="Phone" />
               <PasswordInput<SignupFormInputs> label="Password" name="password" placeholder="Password" />

               <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-md bg-orange-500 py-2.5 text-custom-sm-bold  text-white hover:bg-orange-600"
               >
                  {isLoading ? "Creating account..." : "Create Account"}
               </button>
            </form>
         </FormProvider>

         <div className="mt-6">
            <SocialLoginSection page="signup" />
         </div>
      </div>
   )
}

export default SignupForm
